import { Field, Fields } from "redux-form";

import { Box, Typography } from "@braintrust/braintrust-ui-components";
import { useIsNodeOrImpersonated } from "@js/apps/common/hooks/use-is-node-or-impersonated";
import { CanManageOnBehalfOfClientModule } from "@js/apps/jobs/apps/create-job/components/can-manage-job-on-behalf-of-client-module";
import { JobDescriptionUpdateStatusTag } from "@js/apps/jobs/apps/create-job/components/job-description-update-status-tag";
import {
  JobRoleField,
  JobTypeField,
  PaymentConditionsField,
} from "@js/apps/jobs/apps/create-job/fields";
import { OpeningsNumberField } from "@js/apps/jobs/apps/create-job/fields/openings-number-field";
import {
  jobTitleLengthValidator,
  jobTitleSignValidator,
  jobTitleValidator,
  maxOpeningsNumberValidator,
} from "@js/apps/jobs/apps/create-job/validators";
import {
  useCanManageJobsOnBehalfOfClient,
  useEmployerMaxOpeningsNumber,
} from "@js/apps/jobs/hooks";
import { TextField } from "@js/forms/fields";
import { required } from "@js/forms/validators";

type CreateOrEditJobSetUpFieldsProps = { isEditForm?: boolean };

export const CreateOrEditJobSetUpFields = ({
  isEditForm,
}: CreateOrEditJobSetUpFieldsProps) => {
  const isBadgeDisplayed = useCanManageJobsOnBehalfOfClient();
  const maxOpeningsNumber = useEmployerMaxOpeningsNumber();
  const isNodeStaffOrImpersonated = useIsNodeOrImpersonated();

  return (
    <Box sx={{ pb: isBadgeDisplayed ? { xs: 10, sm: 8, xl: 0 } : 0 }}>
      <CanManageOnBehalfOfClientModule badgeOnly={false} />

      <JobDescriptionUpdateStatusTag />

      <Typography component="h1" variant="title" size="large" fontWeight={400}>
        Let's set up your new job
      </Typography>

      <Box sx={{ mb: 5, mt: 7 }}>
        <Field
          name="title"
          id="title"
          validate={[
            jobTitleValidator,
            jobTitleLengthValidator,
            jobTitleSignValidator,
          ]}
          component={TextField}
          variant="standard"
          size="large"
          label="Job title"
          inputTextSize="large"
          placeholder="Enter job title here"
          floatingLabel={false}
          slotProps={{
            inputLabel: {
              sx: {
                transform: "translate(0, -1.5px) scale(1.0)",
              },
            },
          }}
        />
      </Box>

      <Box sx={{ mb: 6 }}>
        <Fields
          names={["job_type", "sub_type"]}
          validate={{
            job_type: [required],
          }}
          component={JobTypeField}
        />
      </Box>

      {isNodeStaffOrImpersonated && (
        <Field name="payment_conditions" component={PaymentConditionsField} />
      )}

      <Field
        name="openings_number"
        component={OpeningsNumberField}
        validate={[required, maxOpeningsNumberValidator(maxOpeningsNumber)]}
        labelProps={{ sx: { mb: 2 } }}
        isEditForm={isEditForm}
      />

      <JobRoleField hideError />
    </Box>
  );
};
